import toc from "markdown-toc";
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from "next";
import Head from "next/head";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemoteSerializeResult } from "next-mdx-remote";
import gfm from "remark-gfm";
import SiteChrome from "@chrome/SiteChrome";
import { HtmlPageTitle } from "@components/HtmlPageTitle";
import { SiteHtmlPageTitle } from "@content/en/HtmlTitle";
import { ParsedUrlQuery } from "querystring";
import BlogMetaData from "@components/blog/BlogMetaData";
import BlogPost from "@components/blog/BlogPost";
import BlogPostContent from "@components/blog/BlogPostContent";
import { getAllBlogArticles, getBlogArticleFromSlug } from "@scripts/mdx";
import { stringToURI } from "@scripts/utils";

interface BlogPostPageProps {
  metadata: BlogPost;
  postSource: MDXRemoteSerializeResult;
  tocSource: MDXRemoteSerializeResult;
}

const BlogPostPage = ({
  metadata,
  postSource,
  tocSource,
}: BlogPostPageProps) => (
  <>
    {metadata.canonicalUrl && (
      <Head>
        <link rel="canonical" href={metadata.canonicalUrl} />
      </Head>
    )}
    <SiteChrome noPadding>
      <HtmlPageTitle title={`${metadata.title} - ${SiteHtmlPageTitle}`} />
      <BlogMetaData
        description={metadata.excerpt}
        title={metadata.title}
        author={metadata.author}
        image={metadata.banner}
        slug={metadata.slug}
      />
      <BlogPostContent
        metadata={metadata}
        postSource={postSource}
        tocSource={tocSource}
      />
    </SiteChrome>
  </>
);

export const getStaticPaths: GetStaticPaths = async () => {
  const allArticles = await getAllBlogArticles();

  const paths = allArticles.map((article) => ({
    params: {
      slug: article.slug,
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

interface Params extends ParsedUrlQuery {
  slug: string;
}

export const getStaticProps: GetStaticProps = async ({
  params,
}: GetStaticPropsContext) => {
  const { slug } = params as Params;
  const metadata = await getBlogArticleFromSlug(slug);

  const postSource = await serialize(metadata.content, {
    mdxOptions: { remarkPlugins: [gfm] },
  });
  const tocSource = await serialize(
    toc(metadata.content, { maxdepth: 3, slugify: stringToURI }).content,
  );

  return {
    props: {
      metadata,
      postSource,
      tocSource,
    },
  };
};

export default BlogPostPage;
